import React from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonProps } from './Button';

interface ActionMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerLabel: string;
  triggerTitle?: string;
  triggerIcon: React.ReactNode;
  children: React.ReactNode;
  width?: number;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  buttonClassName?: string;
  triggerChildren?: React.ReactNode;
}

const viewportPadding = 8;

export function ActionMenu({
  open,
  onOpenChange,
  triggerLabel,
  triggerTitle = triggerLabel,
  triggerIcon,
  children,
  width = 224,
  buttonVariant = 'ghost',
  buttonSize = 'icon',
  buttonClassName,
  triggerChildren,
}: ActionMenuProps) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuId = React.useId();
  const [position, setPosition] = React.useState({ top: 0, left: 0, placement: 'bottom' as 'top' | 'bottom' });

  const updatePosition = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const left = Math.min(
      Math.max(viewportPadding, rect.right - width),
      window.innerWidth - width - viewportPadding,
    );
    const opensUp = rect.bottom > window.innerHeight * 0.65;
    const top = opensUp ? rect.top - viewportPadding : rect.bottom + viewportPadding;

    setPosition({ top, left, placement: opensUp ? 'top' : 'bottom' });
  }, [width]);

  React.useLayoutEffect(() => {
    if (!open) {
      return;
    }

    updatePosition();
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenChange, open, updatePosition]);

  return (
    <>
      <Button
        ref={triggerRef}
        variant={buttonVariant}
        size={buttonSize}
        className={buttonClassName}
        icon={triggerIcon}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={triggerTitle}
        onClick={() => onOpenChange(!open)}
      >
        {triggerChildren}
      </Button>

      {open
        ? createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={() => onOpenChange(false)} />
            <div
              id={menuId}
              role="menu"
              className="fixed z-50 overflow-hidden rounded-lg border border-ink-200 bg-white py-1 text-left shadow-raised"
              style={{
                top: position.top,
                left: position.left,
                width,
                maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
                transform: position.placement === 'top' ? 'translateY(-100%)' : undefined,
              }}
            >
              {children}
            </div>
          </>,
          document.body,
        )
        : null}
    </>
  );
}
